import {
  ReactNode,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Box, Grid, IconButton, Menu } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { formatDate } from '@roc/feature-utils';
import { DataGrid, GridColumnProps, SelectMode } from '@roc/ui';
import { formatCurrency } from '@roc/ui/utils';
import { observer } from 'mobx-react';
import { MoreVert } from '@material-ui/icons';

const useStyles = makeStyles(() => ({
  gridContainer: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    height: '65vh',
    overflow: 'hidden',
  },
}));

interface PostClosingGridProps {
  store: any;
  columns: GridColumnProps[];
  selectModeEnabled: boolean;
  toolbar?: ReactNode;
  actions: (record: any, handleMenuClose: () => void) => ReactNode;
}

export const PostClosingGrid = observer(
  forwardRef(
    (
      {
        store,
        columns,
        selectModeEnabled,
        toolbar,
        actions,
      }: PostClosingGridProps,
      ref
    ) => {
      const classes = useStyles();
      const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
      const [record, setRecord] = useState<any>({});
      const gridApiRef = useRef<any>(null);

      const menuOpen = Boolean(anchorEl);

      const onGridReady = params => {
        gridApiRef.current = params.api;
      };

      const handleMenuClose = () => setAnchorEl(null);

      const openMenu = (event: React.MouseEvent<HTMLElement>, record: any) => {
        setRecord(record);
        setAnchorEl(event.currentTarget);
      };

      const frameworkComponents = {
        dateCellRenderer: ({ value }) =>
          value ? formatDate(value, 'MM/dd/yyyy') : '',
        currencyCellRenderer: ({ value }) => formatCurrency(value),
        booleanCellRenderer: ({ value }) => (value ? 'Yes' : 'No'),
        integerCellRenderer: ({ value }) => {
          if (value === null || value === undefined) return null;
          const displayValue =
            typeof value === 'number' ? value.toFixed(0) : parseInt(value, 10);
          return <span>{displayValue}</span>;
        },
        actionCellRenderer: params => (
          <IconButton onClick={e => openMenu(e, params.node.data)}>
            <MoreVert color="primary" />
          </IconButton>
        ),
        loanIdLinkRenderer: ({ value }) => {
          const parentOrigin = window.location.ancestorOrigins[0];

          const isLocalhost = parentOrigin.includes('localhost:3000');
          const basePath = isLocalhost
            ? `${parentOrigin}/#/loan_information/${value}`
            : `${parentOrigin}/nportal#/loan_information/${value}`;

          return (
            <span
              style={{
                color: '#1a73e8',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              onClick={() => {
                window.open(basePath, '_blank');
              }}
            >
              {value}
            </span>
          );
        },
      };

      const onRowSelected = params => {
        const id = params?.data?.recordId || params?.data?.assignedLoanId;
        store?.setSelectedLoanId?.(id, params?.node?.selected);
      };

      const onSortChanged = params => {
        store?.postClosingGridStore?.setSortModel?.(params.api.getSortModel());
      };

      const onFilterChanged = params => {
        store?.postClosingGridStore?.setFilterModel?.(
          params.api.getFilterModel()
        );
      };

      useEffect(() => {
        if (
          selectModeEnabled &&
          typeof store?.clearSelectedLoanIds === 'function'
        ) {
          store.clearSelectedLoanIds();
          setTimeout(() => {
            if (gridApiRef.current?.deselectAll) {
              gridApiRef.current.deselectAll();
            }
          }, 100);
        }
      }, [store.tab]);

      useImperativeHandle(ref, () => ({
        deselectAll: () => gridApiRef.current?.deselectAll?.(),
        refresh: () => gridApiRef.current?.refreshCells?.({ force: true }),
      }));

      const rows = store?.postClosingGridStore?.gridData?.data?.rows || [];

      return (
        <Box>
          <Grid item className={classes.gridContainer}>
            <DataGrid
              columns={columns}
              rows={rows}
              frameworkComponents={frameworkComponents}
              selectMode={selectModeEnabled ? SelectMode.MULTIPLE : undefined}
              headerCheckboxSelection={selectModeEnabled}
              suppressRowClickSelection={selectModeEnabled}
              toolbar={toolbar}
              key={store.tab}
              onSortChanged={onSortChanged}
              onFilterChanged={onFilterChanged}
              isLoading={store?.postClosingGridStore?.isLoading}
              showFilters={store?.postClosingGridStore?.showFilters}
              sortModel={store?.postClosingGridStore?.sortModel}
              filterModel={store?.postClosingGridStore?.filterModel}
              paginationData={store?.postClosingGridStore?.gridData?.meta}
              setPageNumber={store?.postClosingGridStore?.setPageNumber}
              onRowSelected={onRowSelected}
              gridReady={onGridReady}
            />
          </Grid>

          <Menu
            id="post-closing-menu"
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            keepMounted
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            getContentAnchorEl={null}
          >
            {actions(record, handleMenuClose)}
          </Menu>
        </Box>
      );
    }
  )
);
